/**
 * <MissionMap> — the shared operating-picture map used by Plan / Live / Replay / A2A.
 * Props follow docs/CONTRACT.md (`MissionMapProps`) plus a few additive options (see lib/types).
 * The heavy lifting lives in MapController; this component only bridges React props.
 */
import { useIsPhone, useMediaQuery } from '../../app/useMediaQuery';
import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Marker, TrafficTarget } from '@apos/shared';
import type { BasemapId, MapFocusRequest, MapLayerGroup, MissionMapProps } from '../../lib/types';
import { usePrefs } from '../../lib/prefs';
import { useTraffic } from '../../state/traffic';
import { useMission } from '../../state/mission';
import { MapController, type ControllerCallbacks, type ControllerProps, type EdgeInfo, type HiddenGroup } from './MapController';
import { TERRAIN_UNAVAILABLE_TEXT, type TerrainStatus, type ViewMode } from './terrain3d';
import { IconButton } from '../ui';
import { MapFrame } from './MapFrame';
import { MapControls } from './MapControls';
import { FieldMapLayers } from '../../features/conditions/FieldMapLayers';

export type { MissionMapProps, MapFocusRequest } from '../../lib/types';

/** MissionMap's props: the contract (lib/types) plus the 3D additions (VLP-2300). */
export type MissionMapViewProps = MissionMapProps & {
  /** When `key` changes: ease to the point (3D keeps its pitch) and show a hollow ghost there for ~3 s. */
  focusRequest?: MapFocusRequest | null;
  /** Controlled 2D/3D view; omit to let the map's own toggle own it. */
  viewMode?: ViewMode;
  onViewModeChange?(mode: ViewMode): void;
};

const NO_PAIRS: NonNullable<MissionMapProps['pairs']> = [];
const NO_MARKERS: Marker[] = [];
const NO_TRAFFIC: TrafficTarget[] = [];
/** Groups the controller hides by layer visibility (the others are existing props). */
const HIDEABLE = ['cones', 'cameras', 'markers'] as const satisfies readonly MapLayerGroup[];
const NO_GROUPS: HiddenGroup[] = [];

export function MissionMap(props: MissionMapViewProps) {
  const trafficNote=useTraffic(s=>s.missionId===props.bundle.mission.id?s.notes:null);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const ctrlRef = useRef<MapController | null>(null);
  const [fieldMap,setFieldMap]=useState<import('maplibre-gl').Map|null>(null);
  const prefBasemap = usePrefs((s) => s.basemap);
  const setPref = usePrefs((s) => s.set);
  const trackUp = usePrefs((s) => s.trackUp);
  const prefTraffic = usePrefs((s) => s.showTraffic);
  const prefPath = usePrefs((s) => s.showFlightPath);
  // viewing mode (LIVE): a group set to false is hidden whatever the other props / switches say
  const modeLayers = props.layers;
  const off = (g: MapLayerGroup) => modeLayers?.[g] === false;
  const showFlightPath = (props.showFlightPath ?? prefPath) && !off('flightPath');
  const hiddenKey = HIDEABLE.filter(off).join(',');
  const hiddenGroups = useMemo(() => (hiddenKey ? (hiddenKey.split(',') as HiddenGroup[]) : NO_GROUPS), [hiddenKey]);
  const showTraffic = props.traffic !== undefined && prefTraffic && !off('traffic');
  // flight paths come from the open mission's record (Live and Replay share it)
  const storePathObs = useMission((s) => (showFlightPath && s.record && s.record.mission.id === props.bundle.mission.id ? s.record.aircraftObs : null));
  const storePathSince = useMission((s) => (s.record?.mission.id === props.bundle.mission.id ? s.record.mission.actualStart : null)) ?? props.bundle.mission.actualStart;
  const pathObs = props.pathObs !== undefined ? (showFlightPath ? props.pathObs : null) : storePathObs;
  const pathSince = props.pathObs !== undefined ? (props.pathSince ?? null) : storePathSince;
  const basemap: BasemapId = props.basemap ?? prefBasemap;
  const [view, setView] = useState({ bearing: 0, zoom: 11.5, offline: false, loaded: false });
  const [edge, setEdge] = useState<EdgeInfo | null>(null);
  const prefMapView = usePrefs((s) => s.mapView);
  const viewMode: ViewMode = props.viewMode ?? prefMapView;
  const [terrain, setTerrain] = useState<TerrainStatus>('off');
  const [forecastOff, setForecastOff] = useState<string | null>(null);

  // a phone held sideways shows the LIVE sheet as a left side panel (live.css): frame beside it, not above it
  const phone = useIsPhone();
  const sideways = useMediaQuery('(orientation: landscape) and (max-height: 500px) and (pointer: coarse)');
  const padding = useMemo(() => {
    if (expanded) return { top: 12, right: 12, bottom: 12, left: 12 };
    const p = { top: props.padding?.top ?? 0, right: props.padding?.right ?? 0, bottom: props.padding?.bottom ?? 0, left: props.padding?.left ?? 0 };
    if (sideways && p.bottom > 100) return { top: 64, right: p.right, bottom: 76, left: Math.min(380, Math.round(window.innerWidth * 0.44)) + 8 };
    // LIVE phone (a bottom sheet): also clear the full-width MARK row above the sheet and the chips at the top
    if (phone && p.bottom > 100) return { ...p, top: Math.max(p.top, 64), bottom: p.bottom + 80 };
    return p;
  }, [expanded, sideways, phone, props.padding?.top, props.padding?.right, props.padding?.bottom, props.padding?.left],
  );

  const cprops: ControllerProps = {
    bundle: props.bundle,
    world: props.world,
    selection: props.selection,
    pairs: props.pairs ?? NO_PAIRS,
    showSunRay: (props.showSunRay ?? false) && !off('sunRay'),
    sunRayFrom: props.sunRayFrom ?? null,
    followAircraftId: props.followAircraftId ?? null,
    basemap,
    markers: props.markers ?? NO_MARKERS,
    showRangeRings: (props.showRangeRings ?? true) && !off('rings'),
    showLeaders: props.showLeaders ?? true,
    myHeadingDeg: props.myHeadingDeg ?? null,
    trackUp,
    padding,
    traffic: props.traffic === undefined ? null : (props.traffic ?? NO_TRAFFIC),
    showTraffic,
    showFlightPath,
    pathObs,
    pathSince,
    cameraCoverage: props.cameraCoverage ?? null, // coverage lane
    findAircraftId: props.find?.aircraftId ?? null,
    aircraftStatus: props.aircraftStatus ?? null,
    hiddenGroups,
    fitAirports: props.fitAirports,
    quietLost: props.quietLost,
    frame: props.frame,
    viewMode,
  };
  const callbacks: ControllerCallbacks = {
    onSelect: props.onSelect,
    onMapClick: props.interactive?.onMapClick,
    onFollowBreak: () => props.onFollowChange?.(null),
    onViewChange: setView,
    onEdge: setEdge,
    onTerrain: setTerrain,
    onForecastOff: setForecastOff,
  };
  const latest = useRef<{ cprops: ControllerProps; callbacks: ControllerCallbacks; find?: MissionMapProps['find'] }>({ cprops, callbacks });
  latest.current = { cprops, callbacks, find: props.find ?? null };

  // create once
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let ctrl: MapController;
    try {
      ctrl = new MapController(el, latest.current.cprops, latest.current.callbacks);
    } catch (e) {
      console.error('[map] failed to initialise', e);
      return;
    }
    ctrlRef.current = ctrl;
    setFieldMap(ctrl.map);
    props.onMapReady?.(ctrl.map);
    const ro = new ResizeObserver(() => ctrl.resize());
    ro.observe(el);
    return () => {
      ro.disconnect();
      ctrl.destroy();
      ctrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // push props every render (controller diffs cheaply by identity)
  useEffect(() => {
    ctrlRef.current?.setProps(latest.current.cprops, latest.current.callbacks);
  });

  // find: each new seq frames the aircraft with me / the reference
  const findSeq = props.find?.seq ?? 0;
  useEffect(() => {
    const f = latest.current.find;
    if (findSeq > 0 && f) ctrlRef.current?.frameAircraft(f.aircraftId, f.with, true);
  }, [findSeq]);

  // focus: each new key eases to the point and leaves a ghost there
  const focus = props.focusRequest ?? null;
  const focusKey = focus?.key ?? null;
  const focusRef = useRef(focus);
  focusRef.current = focus;
  useEffect(() => {
    const f = focusRef.current;
    if (focusKey !== null && f) ctrlRef.current?.focusOn(f.lat, f.lon, f.altM);
  }, [focusKey]);

  const setViewMode = (m: ViewMode) => {
    if (props.viewMode === undefined) setPref('mapView', m);
    props.onViewModeChange?.(m);
  };
  const chips: Array<{ id: string; text: string; testId: string }> = [];
  if (viewMode === '3d' && terrain === 'unavailable') chips.push({ id: 'terrain', text: TERRAIN_UNAVAILABLE_TEXT, testId: 'map-terrain-unavailable' });
  if (forecastOff) chips.push({ id: 'forecast', text: forecastOff, testId: 'map-forecast-off' });

  return (
    <MapFrame expanded={expanded} onClose={() => setExpanded(false)}>
      <div
        className={`mission-map${props.className ? ` ${props.className}` : ''}${props.interactive?.onMapClick ? ' is-placing' : ''}`}
        data-basemap={basemap}
        data-traffic={props.traffic === undefined ? undefined : showTraffic ? (props.traffic?.length ?? 0) : 0}
        data-hidden={hiddenKey || undefined}
        data-view={viewMode}
      >
        <div ref={containerRef} className="mission-map__canvas" />
        {props.fieldConditions&&fieldMap&&<FieldMapLayers key={props.bundle.mission.id} map={fieldMap} airport={props.bundle.missionAirports.find(a=>a.role==='primary')?.icao??props.bundle.airports[0]?.icao??null}/>}
        {edge && props.find ? (
          <button
            type="button"
            className={`map-edge map-edge--${edge.side}`}
            style={{ left: edge.x, top: edge.y }}
            onClick={props.find.onTap}
            aria-label={`Find ${props.find.label}${props.find.dist ? `, ${props.find.dist}` : ''} — off screen`}
            data-testid="map-edge"
          >
            <span className="map-edge__arrow" style={{ transform: `rotate(${edge.angleDeg}deg)` }} aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M12 2l8 18-8-4.5L4 20z" fill="currentColor" /></svg>
            </span>
            <span className="map-edge__label">{props.find.label}</span>
            {props.find.dist ? <span className="map-edge__dist num">{props.find.dist}</span> : null}
          </button>
        ) : null}
        {chips.length ? (
          <div className="map-status-chips" style={{ top: padding.top + (view.offline ? 44 : 12) }} role="status">
            {chips.map((c) => (
              <span key={c.id} className="map-status-chip" data-testid={c.testId}>
                {c.text}
              </span>
            ))}
          </div>
        ) : null}
        {view.offline ? (
          <div className="mission-map__offline" role="status">
            OFFLINE BASEMAP
          </div>
        ) : null}
        {props.hideControls && !expanded ? <div className="map-controls"><IconButton icon="fit" label="Full-screen map" variant="overlay" onClick={() => setExpanded(true)} /></div> : null}
        {!props.hideControls || expanded ? (
          <MapControls
            expanded={expanded}
            onToggleExpanded={() => setExpanded((v) => !v)}
            trafficNote={props.traffic!==undefined?trafficNote:null}
            bearing={view.bearing}
            basemap={basemap}
            following={!!props.followAircraftId}
            trackUp={trackUp}
            viewMode={viewMode}
            onViewMode={setViewMode}
            layers={{
              traffic: props.traffic !== undefined ? { on: prefTraffic, onChange: (v) => setPref('showTraffic', v), hiddenBy: off('traffic') ? props.viewName : undefined } : null,
              flightPath: props.showFlightPath === undefined ? { on: prefPath, onChange: (v) => setPref('showFlightPath', v), hiddenBy: off('flightPath') ? props.viewName : undefined } : null,
            }}
            onZoom={(d) => ctrlRef.current?.zoomBy(d)}
            onNorth={() => {
              if (trackUp) setPref('trackUp', false);
              ctrlRef.current?.resetNorth();
            }}
            onToggleTrackUp={() => setPref('trackUp', !trackUp)}
            onFit={() => {
              props.onFollowChange?.(null);
              if (props.onFit) props.onFit();
              else ctrlRef.current?.fitAll(true);
            }}
            onBasemap={(b) => {
              if (props.onBasemapChange) props.onBasemapChange(b);
              else setPref('basemap', b);
            }}
          />
        ) : null}
      </div>
    </MapFrame>
  );
}

export default MissionMap;
